import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, FolderKanban, AlertTriangle,
  Lock, Link2, CheckCircle2, X, Clock, Calendar, Check, Pencil,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { Glass } from '../components/glass/Glass';
import { RoleBadge } from '../components/primitives/Badge';
import { ProgressBar } from '../components/primitives/ProgressBar';

const DAY_WIDTH = 36; // px per day
const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 52;

function dateToKey(d) { return d.toISOString().split('T')[0]; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function diffDays(a, b) { return Math.round((new Date(a) - new Date(b)) / (1000 * 60 * 60 * 24)); }

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ─── Gantt Bar ──────────────────────────────────────────────────────────────

const GanttBar = ({ task, startDay, durationDays, color, isOverdue, isBlocked, isDone, isLocked, onClick }) => {
  const left = startDay * DAY_WIDTH;
  const width = Math.max(durationDays * DAY_WIDTH - 2, 18);

  return (
    <motion.div
      initial={{ opacity: 0, scaleX: 0.5 }}
      animate={{ opacity: 1, scaleX: 1 }}
      whileHover={{ y: -1, boxShadow: T.shadowSm }}
      onClick={onClick}
      style={{
        position: 'absolute', left, top: 4,
        width, height: ROW_HEIGHT - 8,
        borderRadius: 6,
        background: isDone ? 'rgba(90,158,120,0.15)' : isBlocked ? 'rgba(224,88,88,0.08)' : `${color}18`,
        border: `1px solid ${isDone ? T.green : isOverdue ? T.red : isBlocked ? 'rgba(224,88,88,0.25)' : `${color}40`}`,
        borderLeft: `3px solid ${isDone ? T.green : isOverdue ? T.red : color}`,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px',
        overflow: 'hidden', whiteSpace: 'nowrap',
        opacity: isDone ? 0.5 : 1,
        textDecoration: isDone ? 'line-through' : 'none',
      }}
    >
      {isLocked && <Lock size={9} color={T.amber} style={{ flexShrink: 0 }} />}
      {isBlocked && <Link2 size={9} color={T.red} style={{ flexShrink: 0 }} />}
      <span style={{
        fontSize: 10, fontWeight: 500, color: isDone ? T.textMuted : T.text,
        overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {task.title}
      </span>
    </motion.div>
  );
};

// ─── Deadline Marker ────────────────────────────────────────────────────────

const DeadlineMarker = ({ dayOffset }) => (
  <div style={{
    position: 'absolute',
    left: dayOffset * DAY_WIDTH + DAY_WIDTH / 2 - 4,
    top: 2, width: 8, height: ROW_HEIGHT - 4,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      width: 0, height: 0,
      borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
      borderTop: `6px solid ${T.red}`,
    }} />
  </div>
);

// ─── Main GanttView ─────────────────────────────────────────────────────────

// ─── Task Detail Modal ──────────────────────────────────────────────────────

const TaskDetailModal = ({ task, onClose, onUpdate }) => {
  const [title, setTitle] = useState(task.title);
  const [priority, setPriority] = useState(task.priority || 2);
  const [duration, setDuration] = useState(task.duration_minutes || 30);
  const [deadline, setDeadline] = useState(task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '');
  const [saving, setSaving] = useState(false);
  const role = task.roles || task.goals?.roles;
  const r = role?.slug ? ROLES[role.slug] : null;
  const isDone = task.done || task.status === 'done';
  const fmtDur = (m) => m >= 60 ? `${Math.floor(m/60)}h${m%60 ? ` ${m%60}m` : ''}` : `${m}m`;

  const save = async () => {
    setSaving(true);
    await supabase.from('tasks').update({
      title, priority, duration_minutes: duration,
      deadline: deadline ? new Date(deadline + 'T17:00:00Z').toISOString() : null,
    }).eq('id', task.id);
    setSaving(false);
    onUpdate();
    onClose();
  };

  const toggleDone = async () => {
    const newDone = !isDone;
    await supabase.from('tasks').update({
      done: newDone, done_at: newDone ? new Date().toISOString() : null,
      status: newDone ? 'done' : 'todo',
    }).eq('id', task.id);
    onUpdate();
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <motion.div
        initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ width: 420, background: '#fff', borderRadius: 14, padding: 24, boxShadow: T.shadowLg }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Task Details</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textLight }}><X size={16} /></button>
        </div>

        <input value={title} onChange={e => setTitle(e.target.value)} style={{
          width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)',
          fontSize: 14, fontFamily: 'inherit', outline: 'none', marginBottom: 12, fontWeight: 500,
        }} />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          {r && <RoleBadge roleSlug={role.slug} />}
          {task.goals?.title && <span style={{ fontSize: 11, color: T.textMuted }}>in {task.goals.title}</span>}
          {task.milestones?.title && <span style={{ fontSize: 10, color: T.blue }}>/ {task.milestones.title}</span>}
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 3 }}>
            {[1, 2, 3].map(p => (
              <button key={p} onClick={() => setPriority(p)} style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                background: priority === p ? (p === 1 ? T.redLight : p === 2 ? T.amberLight : 'rgba(0,0,0,0.04)') : 'rgba(0,0,0,0.02)',
                color: priority === p ? (p === 1 ? T.red : p === 2 ? T.amber : T.textMuted) : T.textLight,
                border: `1px solid ${priority === p ? 'rgba(0,0,0,0.10)' : 'rgba(0,0,0,0.04)'}`,
              }}>P{p}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} color={T.textMuted} />
            <select value={duration} onChange={e => setDuration(Number(e.target.value))} style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '4px 6px',
              fontSize: 11, fontFamily: 'inherit', background: '#fff', color: T.textMid,
            }}>
              {[15,30,60,120,240,480].map(v => <option key={v} value={v}>{fmtDur(v)}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Calendar size={12} color={T.textMuted} />
            <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '4px 6px',
              fontSize: 11, fontFamily: 'inherit', background: '#fff', color: T.textMid,
            }} />
          </div>
        </div>

        {task.blocker_ids?.length > 0 && (
          <div style={{ fontSize: 11, color: T.red, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Link2 size={10} /> Blocked by {task.blocker_ids.length} task{task.blocker_ids.length > 1 ? 's' : ''}
          </div>
        )}

        {task.eta && (
          <div style={{ fontSize: 11, color: T.blue, marginBottom: 12 }}>
            ETA: {new Date(task.eta).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <button onClick={toggleDone} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '7px 14px', borderRadius: 8, border: `1px solid ${isDone ? 'rgba(224,88,88,0.20)' : 'rgba(90,158,120,0.20)'}`,
            background: 'transparent', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
            color: isDone ? T.red : T.green,
          }}>
            {isDone ? <X size={12} /> : <Check size={12} />}
            {isDone ? 'Reopen' : 'Complete'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
              background: 'transparent', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: T.textMid,
            }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{
              padding: '7px 14px', borderRadius: 8, border: 'none',
              background: T.amber, color: '#fff', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            }}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const GanttView = ({ goals, milestones, tasks, scheduledBlocks, onUpdate }) => {
  const [windowStart, setWindowStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return dateToKey(d);
  });
  const [hoveredTask, setHoveredTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const windowDays = 42; // 6 weeks
  const today = dateToKey(new Date());

  // Build day columns
  const days = useMemo(() => {
    const result = [];
    for (let i = 0; i < windowDays; i++) {
      const d = addDays(new Date(windowStart + 'T12:00:00'), i);
      result.push({
        key: dateToKey(d),
        date: d,
        dayOfMonth: d.getDate(),
        dayOfWeek: d.getDay(),
        month: d.getMonth(),
        isToday: dateToKey(d) === today,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
      });
    }
    return result;
  }, [windowStart, today]);

  // Group tasks by project > stage
  const projects = goals.filter(g => g.is_project);
  const ungroupedTasks = tasks.filter(t => !t.goal_id || !projects.find(p => p.id === t.goal_id));

  // Build row data
  const rows = useMemo(() => {
    const result = [];

    for (const project of projects) {
      const projectStages = milestones
        .filter(m => m.goal_id === project.id)
        .sort((a, b) => a.sort_order - b.sort_order);

      const projectTasks = tasks.filter(t => t.goal_id === project.id);
      const doneTasks = projectTasks.filter(t => t.done || t.status === 'done');
      const progress = projectTasks.length > 0 ? Math.round((doneTasks.length / projectTasks.length) * 100) : 0;

      // Project header row
      result.push({ type: 'project', project, progress, taskCount: projectTasks.length });

      for (const stage of projectStages) {
        const stageTasks = tasks.filter(t => t.milestone_id === stage.id);
        if (stageTasks.length === 0) continue;

        // Stage header row
        result.push({ type: 'stage', stage, project });

        for (const task of stageTasks) {
          result.push({ type: 'task', task, project, stage });
        }
      }

      // Unstaged tasks for this project
      const unstaged = projectTasks.filter(t => !t.milestone_id);
      for (const task of unstaged) {
        result.push({ type: 'task', task, project, stage: null });
      }
    }

    // Ungrouped tasks
    if (ungroupedTasks.length > 0) {
      result.push({ type: 'project', project: { title: 'Ungrouped Tasks', id: '__ungrouped' }, progress: 0, taskCount: ungroupedTasks.length });
      for (const task of ungroupedTasks.filter(t => !t.done && t.status !== 'done').slice(0, 20)) {
        result.push({ type: 'task', task, project: null, stage: null });
      }
    }

    return result;
  }, [projects, milestones, tasks, ungroupedTasks]);

  // Compute task bar positions
  const getTaskBar = (task) => {
    // Find scheduled block for this task
    const block = (scheduledBlocks || []).find(b => b.task_id === task.id);
    const eta = task.eta ? dateToKey(new Date(task.eta)) : null;

    let startDate, endDate;

    if (block) {
      startDate = block.date;
      endDate = block.date; // Same day for a single block
    } else if (eta) {
      // Estimate: place bar ending at ETA
      const durDays = Math.max(1, Math.ceil((task.duration_minutes || 30) / (8 * 60)));
      startDate = dateToKey(addDays(new Date(eta + 'T12:00:00'), -durDays + 1));
      endDate = eta;
    } else if (task.deadline) {
      const dl = dateToKey(new Date(task.deadline));
      const durDays = Math.max(1, Math.ceil((task.duration_minutes || 30) / (8 * 60)));
      startDate = dateToKey(addDays(new Date(dl + 'T12:00:00'), -durDays));
      endDate = dl;
    } else {
      // No scheduling info — place at today
      startDate = today;
      const durDays = Math.max(1, Math.ceil((task.duration_minutes || 30) / (8 * 60)));
      endDate = dateToKey(addDays(new Date(today + 'T12:00:00'), durDays - 1));
    }

    const startDay = diffDays(startDate, windowStart);
    const endDay = diffDays(endDate, windowStart) + 1;
    const deadlineDay = task.deadline ? diffDays(dateToKey(new Date(task.deadline)), windowStart) : null;

    return { startDay, durationDays: Math.max(endDay - startDay, 1), deadlineDay };
  };

  const totalWidth = windowDays * DAY_WIDTH;
  const todayOffset = diffDays(today, windowStart);

  const navigate = (dir) => {
    setWindowStart(dateToKey(addDays(new Date(windowStart + 'T12:00:00'), dir * 14)));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Timeline</h1>
          <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
            {projects.length} projects · {tasks.filter(t => !t.done).length} open tasks
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setWindowStart(dateToKey(addDays(new Date(), -2)))} style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500,
            color: T.textMid, fontFamily: 'inherit',
          }}>Today</button>
          <button onClick={() => navigate(-1)} style={{
            padding: '6px 8px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'transparent', cursor: 'pointer', color: T.textMid,
          }}><ChevronLeft size={16} /></button>
          <button onClick={() => navigate(1)} style={{
            padding: '6px 8px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'transparent', cursor: 'pointer', color: T.textMid,
          }}><ChevronRight size={16} /></button>
        </div>
      </div>

      <Glass style={{ padding: 0, overflow: 'hidden' }} hover={false}>
        <div style={{ display: 'flex' }}>
          {/* Left panel: task names */}
          <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid rgba(0,0,0,0.06)', zIndex: 2, background: '#fff' }}>
            {/* Date header spacer */}
            <div style={{ height: HEADER_HEIGHT, borderBottom: '1px solid rgba(0,0,0,0.06)' }} />

            {/* Row labels */}
            {rows.map((row, i) => {
              if (row.type === 'project') {
                const r = row.project.roles?.slug ? ROLES[row.project.roles.slug] : null;
                return (
                  <div key={`p-${i}`} style={{
                    height: ROW_HEIGHT, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '0 12px', background: 'rgba(0,0,0,0.02)',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                  }}>
                    <FolderKanban size={12} color={r?.color || T.amber} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.project.title}
                    </span>
                    {row.progress > 0 && (
                      <span style={{ fontSize: 9, color: T.textMuted, flexShrink: 0 }}>{row.progress}%</span>
                    )}
                  </div>
                );
              }
              if (row.type === 'stage') {
                const isActive = row.stage.is_active || row.stage.stage_status === 'active';
                const isDone = row.stage.stage_status === 'completed' || row.stage.done;
                return (
                  <div key={`s-${i}`} style={{
                    height: ROW_HEIGHT, display: 'flex', alignItems: 'center', gap: 6,
                    padding: '0 12px 0 24px',
                    borderBottom: '1px solid rgba(0,0,0,0.03)',
                  }}>
                    <div style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: isDone ? T.green : isActive ? T.blue : 'rgba(0,0,0,0.10)',
                    }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: isDone ? T.green : isActive ? T.blue : T.textLight }}>
                      {row.stage.title}
                    </span>
                  </div>
                );
              }
              // Task row
              const task = row.task;
              const role = task.roles || task.goals?.roles;
              const isDone = task.done || task.status === 'done';
              return (
                <div key={`t-${i}`} style={{
                  height: ROW_HEIGHT, display: 'flex', alignItems: 'center', gap: 6,
                  padding: '0 12px 0 36px',
                  borderBottom: '1px solid rgba(0,0,0,0.02)',
                  background: hoveredTask === task.id ? 'rgba(212,146,30,0.04)' : 'transparent',
                }}
                  onMouseEnter={() => setHoveredTask(task.id)}
                  onMouseLeave={() => setHoveredTask(null)}
                >
                  {isDone ? <CheckCircle2 size={10} color={T.green} /> : (
                    <div style={{ width: 10, height: 10, borderRadius: 3, border: '1.5px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
                  )}
                  <span style={{
                    fontSize: 11, color: isDone ? T.textMuted : T.text,
                    textDecoration: isDone ? 'line-through' : 'none',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                  }}>
                    {task.title}
                  </span>
                  {task.priority === 1 && !isDone && (
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.red, flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Right panel: timeline */}
          <div style={{ flex: 1, overflowX: 'auto' }}>
            <div style={{ width: totalWidth, position: 'relative' }}>
              {/* Date headers */}
              <div style={{ height: HEADER_HEIGHT, display: 'flex', borderBottom: '1px solid rgba(0,0,0,0.06)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                {days.map((d, i) => {
                  const showMonth = i === 0 || d.dayOfMonth === 1;
                  return (
                    <div key={d.key} style={{
                      width: DAY_WIDTH, flexShrink: 0, textAlign: 'center',
                      borderRight: '1px solid rgba(0,0,0,0.03)',
                      background: d.isToday ? 'rgba(212,146,30,0.06)' : d.isWeekend ? 'rgba(0,0,0,0.015)' : 'transparent',
                    }}>
                      {showMonth && (
                        <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, marginTop: 4, letterSpacing: '0.04em' }}>
                          {MONTH_NAMES[d.month]}
                        </div>
                      )}
                      <div style={{
                        fontSize: 12, fontWeight: d.isToday ? 700 : 500,
                        color: d.isToday ? T.amber : d.isWeekend ? T.textLight : T.textMid,
                        marginTop: showMonth ? 2 : 12,
                      }}>
                        {d.dayOfMonth}
                      </div>
                      <div style={{ fontSize: 8, color: T.textLight, marginTop: 1 }}>
                        {DAY_NAMES[d.dayOfWeek]}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Today line */}
              {todayOffset >= 0 && todayOffset < windowDays && (
                <div style={{
                  position: 'absolute', top: HEADER_HEIGHT,
                  left: todayOffset * DAY_WIDTH + DAY_WIDTH / 2,
                  width: 2, bottom: 0, background: T.red, opacity: 0.3, zIndex: 5,
                }} />
              )}

              {/* Weekend shading */}
              {days.filter(d => d.isWeekend).map(d => {
                const offset = diffDays(d.key, windowStart);
                return (
                  <div key={`we-${d.key}`} style={{
                    position: 'absolute', top: HEADER_HEIGHT,
                    left: offset * DAY_WIDTH, width: DAY_WIDTH, bottom: 0,
                    background: 'rgba(0,0,0,0.015)', zIndex: 0,
                  }} />
                );
              })}

              {/* Task bars */}
              {rows.map((row, i) => {
                const top = HEADER_HEIGHT + i * ROW_HEIGHT;

                if (row.type === 'project') {
                  return (
                    <div key={`pb-${i}`} style={{
                      position: 'absolute', top, left: 0, right: 0,
                      height: ROW_HEIGHT, background: 'rgba(0,0,0,0.02)',
                      borderBottom: '1px solid rgba(0,0,0,0.04)',
                    }} />
                  );
                }

                if (row.type === 'stage') {
                  return (
                    <div key={`sb-${i}`} style={{
                      position: 'absolute', top, left: 0, right: 0,
                      height: ROW_HEIGHT,
                      borderBottom: '1px solid rgba(0,0,0,0.03)',
                    }} />
                  );
                }

                const task = row.task;
                const role = task.roles || task.goals?.roles;
                const r = role?.slug ? ROLES[role.slug] : null;
                const color = r?.color || T.amber;
                const isDone = task.done || task.status === 'done';
                const isBlocked = task.status === 'blocked';
                const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !isDone;

                const bar = getTaskBar(task);

                return (
                  <div key={`tb-${i}`} style={{
                    position: 'absolute', top, left: 0,
                    width: totalWidth, height: ROW_HEIGHT,
                    borderBottom: '1px solid rgba(0,0,0,0.02)',
                    background: hoveredTask === task.id ? 'rgba(212,146,30,0.04)' : 'transparent',
                  }}
                    onMouseEnter={() => setHoveredTask(task.id)}
                    onMouseLeave={() => setHoveredTask(null)}
                  >
                    {bar.startDay >= 0 && bar.startDay < windowDays && (
                      <GanttBar
                        task={task}
                        startDay={bar.startDay}
                        durationDays={bar.durationDays}
                        color={color}
                        isOverdue={isOverdue}
                        isBlocked={isBlocked}
                        isDone={isDone}
                        isLocked={task.is_locked}
                        onClick={(e) => { e.stopPropagation(); setSelectedTask(task); }}
                      />
                    )}
                    {bar.deadlineDay != null && bar.deadlineDay >= 0 && bar.deadlineDay < windowDays && (
                      <DeadlineMarker dayOffset={bar.deadlineDay} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Glass>

      {/* Task detail modal */}
      <AnimatePresence>
        {selectedTask && (
          <TaskDetailModal
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onUpdate={onUpdate}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
